import { Injectable } from "@angular/core";
import { Observable, firstValueFrom, from } from "rxjs";
import { switchMap, map } from "rxjs/operators";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { environment } from "@env/environment";

@Injectable({ providedIn: "root" })
export class ApiService {
  private oauthToken: string | null = null;
  private oauthExpiraEn: number | null = null;

  private discapacidadToken: string | null = null;
  private discapacidadExpiraEn: number | null = null;

  private apiUrl = environment.backendUrl;

  constructor(private http: HttpClient) { }

  private async obtenerOauthToken(): Promise<string> {
    const body = { client_id: environment.clientId };

    const rawResponse = await this.http
      .post(environment.oauthTokenUrl, body, {
        responseType: "text",
      })
      .toPromise();

    const parsed = JSON.parse(rawResponse || "{}");

    this.oauthToken = parsed.access_token;
    const expiresInMs = (parsed.expires_in || 3600) * 1000;
    this.oauthExpiraEn = Date.now() + expiresInMs;

    return this.oauthToken!;
  }

  private async getHeaders(url: string): Promise<HttpHeaders> {
    const ahora = Date.now();
    let token: string;

    if (
      !this.oauthToken ||
      !this.oauthExpiraEn ||
      ahora >= this.oauthExpiraEn
    ) {
      token = await this.obtenerOauthToken();
    } else {
      token = this.oauthToken!;
    }

    return new HttpHeaders({
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json"
    });
  }

  private async privateRequest<T>(method: 'GET' | 'POST' | 'PUT' | 'PATCH', endpoint: string, bodyOrParams: any = {}): Promise<T> {
    const fullUrl = this.apiUrl + endpoint;
    const headers = await this.getHeaders(fullUrl);

    try {
      let request$: Observable<T>;

      switch (method) {
        case 'POST':
          request$ = this.http.post<T>(fullUrl, bodyOrParams, { headers });
          break;
        case 'PUT':
          request$ = this.http.put<T>(fullUrl, bodyOrParams, { headers });
          break;
        case 'PATCH':
          request$ = this.http.patch<T>(fullUrl, bodyOrParams, { headers });
          break;
        default:
          throw new Error(`Método HTTP no soportado: ${method}`);
      }

      return firstValueFrom(request$);
    } catch (error) {
      throw error;
    }
  }

  public privatePost<T>(endpoint: string, body: any = {}): Promise<T> {
    return this.privateRequest<T>('POST', endpoint, body);
  }

  public async privatePostBlob(endpoint: string, body: any = {}): Promise<Blob> {
    const fullUrl = this.apiUrl + endpoint;
    const headers = await this.getHeaders(fullUrl);
    try {
      const request$ = this.http.post(fullUrl, body, { responseType: 'blob', headers });
      return firstValueFrom(request$);
    } catch (error) {
      throw error;
    }
  }

  public privatePut<T>(endpoint: string, body: any = {}): Promise<T> {
    return this.privateRequest<T>('PUT', endpoint, body);
  }

  public privatePatch<T>(endpoint: string, body: any = {}): Promise<T> {
    return this.privateRequest<T>('PATCH', endpoint, body);
  }

  //.subscribe()
  public privatePostObservable<T>(url: string, body: any): Observable<T> {
    return from(this.getHeaders(url)).pipe(
      switchMap(headers => this.http.post<any>(url, body, { headers })),
      map(response => {
        if (typeof response === 'string') {
          try {
            return JSON.parse(response) as T;
          } catch (error) {
            return response as unknown as T;
          }
        }
        return response as T;
      })
    );
  }

  // POST asíncrono no bloqueante para eventos beforeunload/unload
  public async fireAndForgetPost(url: string, body: any): Promise<void> {
    const headers = await this.getHeaders(url);

    // Convierte HttpHeaders Angular → objeto plano para fetch
    const headersObj: Record<string, string> = {};
    headers.keys().forEach((key) => {
      headersObj[key] = headers.get(key) || "";
    });

    try {
      await fetch(url, {
        method: "POST",
        headers: headersObj,
        body: JSON.stringify(body),
        keepalive: true,
      });
    } catch (err) {
      console.error("fireAndForgetPost error:", err);
    }
  }
}
